import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Site-wide footer. Rendered on every page.
 *
 * - Routed pages use React Router <Link> (no full reload).
 * - "Contact Us" is a button that calls onContactClick (opens your ContactModal).
 * - Link labels + column structure are from the approved design; hrefs are
 *   wired to your real routes in App.js.
 *
 * Use Cases lead to existing spending information and frequently asked questions.
 */

const linkStyle = {
  color: 'var(--text-muted)',
  fontSize: '14px',
  textDecoration: 'none',
  transition: 'color 0.2s',
};

const hoverIn = (e) => (e.currentTarget.style.color = 'var(--text)');
const hoverOut = (e) => (e.currentTarget.style.color = 'var(--text-muted)');

// Small helpers so every link shares the same hover behavior
function FLink({ to, children, alignBottom = false }) {
  return (
    <Link to={to} style={{ ...linkStyle, ...(alignBottom ? { marginTop: 'auto' } : {}) }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
      {children}
    </Link>
  );
}

function FButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...linkStyle,
        background: 'none',
        border: 'none',
        padding: 0,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
      onMouseEnter={hoverIn}
      onMouseLeave={hoverOut}
    >
      {children}
    </button>
  );
}

const colStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const headingStyle = {
  color: 'var(--text)',
  fontSize: '15px',
  fontWeight: 700,
};

export default function Footer({ onContactClick }) {
  return (
    <footer className="site-footer"
      style={{
        borderTop: '1px solid var(--border-hairline)',
        padding: '32px clamp(20px,5vw,56px) 28px',
      }}
    >
      <div style={{ maxWidth: 'var(--max-width)', margin: '0 auto', boxSizing: 'border-box' }}>
        <div className="footer-grid"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'clamp(24px,3vw,40px)',
            marginBottom: '24px',
          }}
        >
          {/* Brand block */}
          <div className="footer-brand"
            style={{
              flex: '0 1 300px',
              minWidth: '240px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Link
              to="/"
              aria-label="Home"
              style={{
                color: 'var(--kachng-green)',
                fontWeight: 800,
                fontSize: 'clamp(26px,2.6vw,34px)',
                letterSpacing: '-0.03em',
                textDecoration: 'none',
              }}
            >
              KACHNG
            </Link>
            <div style={{ fontSize: '15px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Every purchase, remembered.
            </div>
            <a
              href="mailto:info@kachngco.com"
              style={{ ...linkStyle, marginTop: 'auto', paddingTop: '24px', overflowWrap: 'anywhere' }}
              onMouseEnter={hoverIn}
              onMouseLeave={hoverOut}
            >
              info@kachngco.com
            </a>
          </div>

          {/* Link columns */}
          <div className="footer-links"
            style={{
              flex: '1 1 520px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))',
              gap: 'clamp(20px,2.5vw,36px)',
            }}
          >
            <div style={colStyle}>
              <div style={headingStyle}>Use Cases</div>
              <FLink to="/#spending">Business Expenses</FLink>
              <FLink to="/#spending">Tax Preparation</FLink>
              <FLink to="/#spending">Budgeting &amp; Analytics</FLink>
              <FLink to="/faq" alignBottom>Returns &amp; Warranties</FLink>
            </div>

            <div style={colStyle}>
              <div style={headingStyle}>Resources</div>
              <FLink to="/manifesto">Manifesto</FLink>
              <FLink to="/lastpaper">The Last Paper</FLink>
              <FLink to="/rewards">Rewards</FLink>
            </div>

            <div style={colStyle}>
              <div style={headingStyle}>Support</div>
              <FButton onClick={onContactClick}>Contact Us</FButton>
              <FLink to="/faq">FAQ</FLink>
            </div>

            <div style={colStyle}>
              <div style={headingStyle}>Legal</div>
              <FLink to="/privacy">Privacy Policy</FLink>
              <FLink to="/terms">Terms and Conditions</FLink>
              <FLink to="/account-deletion">Account &amp; Data Deletion</FLink>
              <FLink to="/sweepstakes-rules" alignBottom>Sweepstakes Rules</FLink>
            </div>
          </div>
        </div>

        <div
          style={{
            borderTop: '1px solid var(--border-hairline)',
            paddingTop: '18px',
            fontSize: '13px',
            color: 'var(--text-dim)',
          }}
        >
          © 2026 KACHNG. All rights reserved
        </div>
      </div>
    </footer>
  );
}